/**
 * The decision the reader may take now, through the one form every review
 * stage shares, or whose turn it is when they have none.
 *
 * Outside the readiness disclosure: the action is the point of the page, and
 * the disclosure starts collapsed on every visit.
 *
 * @module components/governance/GovernanceWorkflowActions
 */

import React, { useState } from "react";

import {
  InitialApprovalFields,
  validateInitialApprovalDetails,
} from "@/components/shared/InitialApprovalFields";
import type { WorkflowQueueAction } from "@/components/shared/workflow-queue-actions";
import { Alert, Button, countLabel, Divider, Text, useConfirm, useToast } from "@/components/ui";
import type { EditorProgrammeMetadata, ProgrammePlatformClient } from "@/platform";
import { openReviewBlockers } from "@/server/review-decision";
import type { ProgrammeReviewRound, WorkflowState } from "@/server/types";

import { readinessBlockerEntries } from "./GovernanceRequirements";
import { COMMENT_HINT, ReviewDecision, type ReviewDecisionOption } from "./ReviewDecision";
import { ReviewOutstanding } from "./ReviewOutstanding";
import type { useProgrammeGovernance } from "./useProgrammeGovernance";

export interface GovernanceWorkflowActionsProps {
  model: ReturnType<typeof useProgrammeGovernance>;
  client: ProgrammePlatformClient;
  programme: EditorProgrammeMetadata;
  hasUnsavedChanges: boolean;
  /** The editor address a readiness blocker is cleared at, or null for none. */
  hrefFor: Parameters<typeof readinessBlockerEntries>[2];
  /** A user's name, from their id. */
  nameFor: (userId: string) => string;
}

/** Names as a sentence: "Reba", "Reba and Brock", "Reba, Brock and Misty". */
function listed(names: readonly string[]): string {
  if (names.length <= 1) {
    return names.join("");
  }
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/** Who acts at this stage, for a reader with no decision to make. */
function whoseTurn(
  workflowState: WorkflowState,
  reviewers: readonly string[],
  missingSignOffs: readonly string[],
): string | undefined {
  const reviewerText = reviewers.length
    ? `the assigned reviewer${reviewers.length === 1 ? "" : "s"} (${listed(reviewers)})`
    : "the assigned reviewer";
  switch (workflowState) {
    case "In Development":
      return "Waiting on the Programme Lead to submit the programme for review.";
    case "Awaiting Internal Review":
      return `Waiting on ${reviewerText} to start the review.`;
    case "Internal Review":
      return `Waiting on ${reviewerText} to record a decision.`;
    case "Changes Required":
      return "Waiting on the Programme Lead to resume development and address the requested changes.";
    case "Awaiting QA Approval":
      return "Waiting on a Client Admin to record the QA approval decision.";
    case "Approved":
      return missingSignOffs.length
        ? `Waiting on the ${listed(missingSignOffs)} sign-off${missingSignOffs.length === 1 ? "" : "s"}.`
        : "Waiting on the Programme Lead to complete final descriptor sign-off.";
    default:
      return undefined;
  }
}

/** Who the reader is acting as, and what the decision at this stage settles. */
function actingAs(workflowState: WorkflowState, round?: ProgrammeReviewRound): string {
  const roundNumber = round?.roundNumber;
  switch (workflowState) {
    case "In Development":
      return "You can submit this programme once every requirement is met. It goes to internal review when a reviewer is assigned, otherwise straight to QA approval.";
    case "Awaiting Internal Review":
      return "You are an assigned reviewer. Start the review to record decisions on it.";
    case "Internal Review":
      return `You are a reviewer${roundNumber ? ` in round ${roundNumber}` : ""}. Record a comment, request changes, or mark the programme reviewed.`;
    case "Changes Required":
      return "Changes were requested. Resume development to address them.";
    case "Awaiting QA Approval":
      return round?.requiredReviewerUserIds.length === 0
        ? "Internal review was skipped because no reviewer was assigned. The programme waits on the QA approval decision."
        : "The programme passed internal review and waits on the QA approval decision.";
    case "Approved":
      return "The programme is approved. Its final descriptor needs the Programme Lead and QA sign-offs.";
    default:
      return "";
  }
}

/** The confirmation a decision asks for before it is recorded, if any. */
function confirmationFor(
  actionId: string,
  openBlockers: number,
  /** A submission that skips internal review for want of a reviewer. */
  toQa: boolean,
): Parameters<ReturnType<typeof useConfirm>>[0] | undefined {
  switch (actionId) {
    case "submit-review":
      return toQa
        ? {
            title: "Submit for QA approval?",
            body: "No internal reviewer is assigned, so internal review is skipped. The submitted revision is fixed and goes to a Client Admin for the QA approval decision.",
            confirmLabel: "Submit for QA approval",
            "data-testid": "submit-review-confirmation",
          }
        : {
            title: "Submit for internal review?",
            body: "The submitted revision is fixed, and a review round opens for the assigned reviewers.",
            confirmLabel: "Submit for internal review",
            "data-testid": "submit-review-confirmation",
          };
    case "mark-reviewed":
      return openBlockers > 0
        ? {
            title: `Mark reviewed with ${countLabel(openBlockers, "open blocker")}?`,
            body: "Your comment is recorded as your attestation for accepting them, and the approver sees it.",
            confirmLabel: "Mark reviewed",
            "data-testid": "mark-reviewed-confirmation",
          }
        : undefined;
    case "approve":
      return {
        title: "Approve this programme?",
        body: "It becomes an approved curriculum revision, which cannot be changed afterwards.",
        confirmLabel: "Approve programme",
        "data-testid": "programme-approval-confirmation",
      };
    case "sign-off-lead":
    case "sign-off-qa":
      return {
        title: `Sign off as ${actionId === "sign-off-lead" ? "programme lead" : "QA"}?`,
        body: "Your sign-off is recorded against the approved descriptor and cannot be withdrawn.",
        confirmLabel: actionId === "sign-off-lead" ? "Sign off as lead" : "Sign off as QA",
        "data-testid": "final-sign-off-confirmation",
      };
    case "complete-final-sign-off":
      return {
        title: "Complete final descriptor sign-off?",
        body: "The programme moves to Final Descriptor Signed Off and is ready for export and lock.",
        confirmLabel: "Complete sign-off",
        "data-testid": "final-descriptor-sign-off-confirmation",
      };
    default:
      return undefined;
  }
}

export function GovernanceWorkflowActions({
  model,
  client,
  programme,
  hasUnsavedChanges,
  hrefFor,
  nameFor,
}: GovernanceWorkflowActionsProps): React.ReactElement | null {
  const confirm = useConfirm();
  const toast = useToast();
  const [attested, setAttested] = useState(false);
  const {
    actionPending,
    approvalActionAvailable,
    approvalBlockers,
    approvalDetails,
    governance,
    leadSigned,
    navigationItems,
    overrideEligibleAction,
    qaSigned,
    reviewRounds,
    runAction,
    setApprovalDetails,
    setError,
    setOverrideActionId,
    setWorkflowNote,
    workflowActions,
    workflowNote,
  } = model;
  if (!governance) {
    return null;
  }
  const latestRound = reviewRounds.at(-1);
  const markingReviewed = workflowActions.some((action) => action.id === "mark-reviewed");
  const reviewBlockers = markingReviewed ? openReviewBlockers(governance.readiness.blockers) : [];
  const attestationNeeded = reviewBlockers.length > 0;
  // Reviewers who accepted open blockers, for the approver deciding next.
  const attestations =
    programme.workflowState === "Awaiting QA Approval"
      ? (latestRound?.outcomes ?? []).filter(
          (outcome) => outcome.outcome === "approved" && (outcome.openBlockerCount ?? 0) > 0,
        )
      : [];

  if (!workflowActions.length) {
    const missingSignOffs = [
      ...(leadSigned ? [] : ["Programme Lead"]),
      ...(qaSigned ? [] : ["QA"]),
    ];
    const waiting = whoseTurn(
      programme.workflowState,
      (latestRound?.round.requiredReviewerUserIds ?? []).map(nameFor),
      missingSignOffs,
    );
    if (!waiting) {
      return null;
    }
    return (
      <ReviewDecision
        actor={<span data-testid="review-decision-waiting">{waiting}</span>}
        decisions={[]}
        comment={workflowNote}
        onCommentChange={setWorkflowNote}
        pending={actionPending}
        onDecide={() => undefined}
      />
    );
  }

  // The override is bound to the saved revision, and an approval override
  // still needs the approval details entered on this panel, not the dialog.
  let overrideRefusal: string | undefined;
  if (actionPending) {
    overrideRefusal = "Wait for the action in progress to finish.";
  } else if (hasUnsavedChanges) {
    overrideRefusal = "Unavailable while there are unsaved changes";
  } else if (overrideEligibleAction?.id === "approve") {
    overrideRefusal =
      validateInitialApprovalDetails(approvalDetails) ??
      (workflowNote.trim() ? undefined : COMMENT_HINT);
  }

  const decisions: ReviewDecisionOption[] = workflowActions.map((action) => ({
    id: action.id,
    label: action.label,
    icon: action.icon,
    tone: action.tone,
    requiresComment: action.requiresNote ?? false,
    refusal:
      action.blockedReason ??
      (action.id === "approve" ? validateInitialApprovalDetails(approvalDetails) : undefined) ??
      (action.id === "mark-reviewed" && attestationNeeded && !attested
        ? `Confirm your comment addresses the ${countLabel(reviewBlockers.length, "open blocker")}.`
        : undefined) ??
      (hasUnsavedChanges ? "Save the programme before recording an outcome" : undefined),
  }));

  const decide = async (decision: ReviewDecisionOption): Promise<void> => {
    const action = workflowActions.find((candidate) => candidate.id === decision.id) as
      WorkflowQueueAction | undefined;
    if (!action) {
      return;
    }
    const comment = workflowNote.trim();
    if (decision.requiresComment && !comment) {
      setError(new Error("Add a comment to record this decision."));
      return;
    }
    const confirmation = confirmationFor(
      action.id,
      reviewBlockers.length,
      action.nextState === "Awaiting QA Approval",
    );
    if (confirmation && !(await confirm(confirmation))) {
      return;
    }
    // The comment is cleared only once it has landed, and only here, so
    // anything typed after a failure stays.
    const landed = await runAction(async () => {
      if (action.id === "sign-off-lead" || action.id === "sign-off-qa") {
        await client.recordFinalSignOff({
          programmeId: programme.id,
          capacity: action.id === "sign-off-lead" ? "lead" : "qa",
          note: comment,
        });
        return;
      }
      if (action.id === "approve") {
        await client.approveProgramme({
          programmeId: programme.id,
          institutionalLabel: approvalDetails.institutionalLabel.trim(),
          effectiveFrom: approvalDetails.effectiveFrom,
          ...(approvalDetails.nextRevalidationDue
            ? { nextRevalidationDue: approvalDetails.nextRevalidationDue }
            : {}),
          note: comment,
        });
        return;
      }
      if (action.outcome) {
        await client.completeReviewAction({
          programmeId: programme.id,
          outcome: action.outcome,
          note: comment,
          ...(action.nextState ? { nextState: action.nextState } : {}),
        });
        return;
      }
      if (action.nextState) {
        await client.transitionWorkflowWithOverride({
          programmeId: programme.id,
          nextState: action.nextState,
          ...(comment ? { note: comment } : {}),
        });
      }
    });
    if (landed && comment) {
      setWorkflowNote("");
      setAttested(false);
    }
    // A comment moves nothing, so nothing else on screen says it landed.
    if (landed && action.id === "comment") {
      toast({
        message: "Comment recorded",
        tone: "success",
        "data-testid": "governance-comment-recorded",
      });
    }
  };

  const outstanding = (
    <ReviewOutstanding
      attestations={attestations}
      nameFor={nameFor}
      approvalBlockers={programme.workflowState === "Awaiting QA Approval" ? approvalBlockers : []}
      reviewBlockers={attestationNeeded ? reviewBlockers : []}
      navigationItems={navigationItems}
      hrefFor={hrefFor}
      attested={attested}
      onAttestedChange={setAttested}
      disabled={actionPending}
    />
  );

  return (
    <>
      <ReviewDecision
        actor={actingAs(programme.workflowState, latestRound?.round)}
        outstanding={outstanding}
        decisions={decisions}
        comment={workflowNote}
        onCommentChange={setWorkflowNote}
        pending={actionPending}
        onDecide={(decision) => {
          decide(decision).catch(() => undefined);
        }}
      >
        {approvalActionAvailable ? (
          <InitialApprovalFields
            idPrefix="governance"
            value={approvalDetails}
            onChange={setApprovalDetails}
            disabled={actionPending}
            disabledReason="Wait for the action in progress to finish."
          />
        ) : null}
      </ReviewDecision>
      {hasUnsavedChanges ? (
        // Visible, not a `title` on a disabled button, matching
        // `ChangeCaseGovernancePanel`. No promise that it clears:
        // `conflict`, `error`, `offline` and `reconnecting` all
        // count as unsaved and none resolve on their own.
        <Alert tone="warning" role="none" className="mt-2" data-testid="governance-unsaved-block">
          Workflow actions are unavailable while this programme has unsaved changes.
        </Alert>
      ) : null}
      {overrideEligibleAction ? (
        <div>
          <Divider className="my-3" />
          <Text tone="muted">
            The normal action remains blocked. Client Admins may record a one-time, revision-bound
            exception without marking these requirements complete.
          </Text>
          <Button
            size="sm"
            tone="danger"
            variant="outline"
            icon="warning"
            className="mt-2"
            data-testid="governance-open-readiness-override"
            disabled={overrideRefusal !== undefined}
            disabledReason={overrideRefusal}
            onClick={() =>
              setOverrideActionId(overrideEligibleAction.id as "submit-review" | "approve")
            }
          >
            Override blockers and continue
          </Button>
        </div>
      ) : null}
    </>
  );
}
